import { useEffect, useRef } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { m } from 'framer-motion';
import Iconify from 'src/components/iconify';
import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import { useTranslate } from 'src/locales';

type BIPEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

export default function DownloadPWAButton({
  sx,
  size = 24,
}: {
  sx?: any;
  size?: number;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();
  const deferred = useRef<BIPEvent | null>(null);

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-ignore for iOS Safari
      (navigator && (navigator as any).standalone));
  const isIOS =
    typeof navigator !== 'undefined' &&
    /iphone|ipad|ipod/i.test(navigator.userAgent || '');

  useEffect(() => {
    if (isStandalone) return () => {};
    const onBIP = (e: Event) => {
      e.preventDefault();
      deferred.current = e as BIPEvent;
    };
    window.addEventListener('beforeinstallprompt', onBIP);
    return () => window.removeEventListener('beforeinstallprompt', onBIP);
  }, [isStandalone]);

  if (isStandalone) return null;

  const handleInstall = async () => {
    try {
      if (isIOS) {
        enqueueSnackbar(t('pwa.promoDescIOS') || 'Add to Home Screen from Share menu.', {
          variant: 'info',
        });
        return;
      }
      if (!deferred.current) {
        enqueueSnackbar(t('pwa.notSupported') || 'Install prompt not available yet.', {
          variant: 'warning',
        });
        return;
      }
      await deferred.current.prompt();
      const choice = await deferred.current.userChoice;
      if (choice.outcome === 'accepted') {
        enqueueSnackbar(t('pwa.installed') || 'App installation started.', {
          variant: 'success',
        });
      }
      deferred.current = null;
    } catch {
      enqueueSnackbar(t('pwa.installError') || 'Unable to start installation.', {
        variant: 'error',
      });
    }
  };

  return (
    <Tooltip title={t('pwa.installApp') || 'Install app'}>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        aria-label="download-app"
        onClick={handleInstall}
        sx={{ width: 40, height: 40, ...sx }}
      >
        <Iconify icon="mdi:download" width={size} />
      </IconButton>
    </Tooltip>
  );
}
